import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  LAB_RESULT_OPS_QUEUE_LABEL,
  type LabResultEscalationRow,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { AdminCloseWorkItem } from "@/components/AdminCloseWorkItem";
import { UnauthorizedError } from "@/lib/clinician-api";
import { labResultEscalationViewRow } from "@/lib/lab-result-escalations";
import { fetchLabResultEscalations } from "@/lib/lab-result-escalations-api";
import { adminPatientHref } from "@/lib/patient-links";

// TES-1401 (Ö-473, Emelie 2026-10-15) — "Saknade provsvar", the named
// operational queue: a blood test whose result has not come after the third
// Webdoc check, or that the doctor reported a remiss or sampling problem
// for. The same case as the doctor's, not a copy: a registered result ends
// it here and there.
//
// Admin only and read-only (TES-1040): the checks happen outside Ella. The
// list names people, so the API logs it as a read of each patient it
// returns, like "Inväntar abonnemangsaktivering".
export function LabResultEscalationsRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [rows, setRows] = useState<LabResultEscalationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows((await fetchLabResultEscalations()).rows);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          {LAB_RESULT_OPS_QUEUE_LABEL}
        </h1>
        <p className="text-sm text-textSecondary max-w-2xl">
          Blodprov där provsvaret inte har kommit efter tredje kontrollen, eller
          där läkaren har angett problem med remiss eller provtagning.
          Kontrollera remiss, genomförd provtagning och överföring från
          LabPortalen/Webdoc. Ärendet lämnar listan när provsvaret är
          registrerat, eller när du stänger arbetsmomentet med en orsak.
          Läkarens ärende och provsvaret påverkas inte. Internt: listan loggas
          som åtkomst till varje person den visar.
        </p>
      </div>
      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}
      {rows === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {rows ? (
        <LabResultEscalationsView
          rows={rows}
          actingRole={claims?.role}
          onClosed={() => void load()}
          onUnauthorized={() => void signOutRef.current()}
        />
      ) : null}
    </div>
  );
}

/** The table itself, or the empty state. */
export function LabResultEscalationsView({
  rows,
  actingRole,
  onClosed,
  onUnauthorized,
}: {
  rows: LabResultEscalationRow[];
  actingRole: string | undefined;
  onClosed: () => void;
  onUnauthorized?: () => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
        Inga saknade provsvar just nu.
      </div>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Patient</th>
            <th className="px-md py-sm font-medium">Orsak</th>
            <th className="px-md py-sm font-medium">I kön sedan</th>
            <th className="px-md py-sm font-medium">Att kontrollera</th>
            <th className="px-md py-sm font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map(labResultEscalationViewRow).map((r) => (
            <tr
              key={r.orderId}
              className="border-t border-surfaceMuted align-top"
            >
              <td className="px-md py-sm font-medium">{r.name}</td>
              <td className="px-md py-sm">
                {r.reason}
                <span className="block text-xs text-textSecondary">
                  {r.checks}
                </span>
              </td>
              <td className="px-md py-sm">{r.escalatedAt}</td>
              <td className="px-md py-sm">
                <ul className="flex flex-col gap-xs">
                  {r.checklist.map((c) => (
                    <li key={c.label}>
                      <span className="font-medium">{c.label}</span>
                      <span className="block text-xs text-textSecondary">
                        {c.facts}
                      </span>
                    </li>
                  ))}
                </ul>
              </td>
              <td className="px-md py-sm">
                <div className="flex flex-col items-start gap-sm">
                  <Link
                    to={adminPatientHref(actingRole, r.patientId)}
                    className="text-brand hover:underline"
                  >
                    Patienten
                  </Link>
                  {/* TES-1400 (Ö-410) — the operational check is closed
                      here; the doctor's waiting item and the result are
                      untouched. */}
                  <AdminCloseWorkItem
                    kind="lab_result_escalation"
                    itemId={r.orderId}
                    onClosed={onClosed}
                    onUnauthorized={onUnauthorized}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
